
import type { DisplayPlayerProp } from "./utils-v2";
import { oppositeStake, rowStakeQuote } from "./utils-v2/displayed-odds";
import type { Stake, StakeQuote } from "@/lib/book-stake";
import type { SportsbookId } from "@/lib/sportsbook-registry";
import { buildOppositeLabel, getSideLabels } from "./odds-labels";
import { isOverSlotSide, isYesNoSide, outcomeLabel } from "@/lib/selection-side";
import { resolveBetLink } from "@/lib/bet-links";

export interface BookOddsData {
  book: string;
  selectionId?: string | null;
  selectionOdds: number | null;
  oppositeOdds: number | null;
  line?: number;
  stake?: StakeQuote | null;
  oppositeStake?: Stake | null;
  betLink?: string | null;
  isBestSelection?: boolean;
  isBestOpposite?: boolean;
}

export interface ComparisonTableData {
  title: string;
  subtitle: string;
  selectionLabel: string;
  oppositeLabel: string;
  mainLine?: number;

  books: BookOddsData[];
  primaryBook?: string;

  bestSelectionOdds: number | null;
  bestOppositeOdds: number | null;

  singleSideOnly: boolean;
}

function getBookOdds(
  row: DisplayPlayerProp,
  bookId: string,
  side: "over" | "under"
): number | null {
  const fieldKey = `${bookId}_${side}_odds` as keyof DisplayPlayerProp;
  const value = row[fieldKey];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return null;
}

function getBookMainOdds(
  row: DisplayPlayerProp,
  bookId: string
): number | null {
  const fieldKey = `${bookId}_odds` as keyof DisplayPlayerProp;
  const value = row[fieldKey];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const betType = (row.bet_type || "").toUpperCase();
  const isOver = betType === "OVER" || betType === "HOME";
  return getBookOdds(row, bookId, isOver ? "over" : "under");
}

function buildGameLineLabel(
  row: DisplayPlayerProp,
  betType: string,
  fallbackLabel: string
): string {
  if (row.selection_label && row.selection_label.trim()) {
    return row.selection_label.trim();
  }

  const team = row.team || row.player_name || "";
  const line = row.line;
  const marketDisplay = row.market_display || row.stat_type || "";

  if (marketDisplay.toLowerCase().includes("spread") && typeof line === "number") {
    const sign = line >= 0 ? "+" : "";
    return team ? `${team} ${sign}${line}` : `${fallbackLabel} ${sign}${line}`;
  }

  if (marketDisplay.toLowerCase().includes("moneyline") || marketDisplay.toLowerCase().includes("ml")) {
    return team || fallbackLabel;
  }

  return team || fallbackLabel;
}

export function displayPlayerPropToComparisonData(
  row: DisplayPlayerProp,
  orderedBooks: SportsbookId[],
  primaryBook?: SportsbookId,
  isMobile = false
): ComparisonTableData {
  const betType = (row.bet_type || "").toUpperCase();
  const isOverUnder = betType === "OVER" || betType === "UNDER" || isYesNoSide(betType);
  const isHomeAway = betType === "HOME" || betType === "AWAY";
  const isOver = isOverSlotSide(betType);
  const { selectionLabel: defaultSelectionLabel, oppositeLabel: defaultOppositeLabel } = getSideLabels(outcomeLabel(row.outcome_key) ?? betType);

  const selectionLabel = isHomeAway
    ? buildGameLineLabel(row, betType, defaultSelectionLabel)
    : defaultSelectionLabel;
  const oppositeLabel = isHomeAway ? buildOppositeLabel(row, defaultOppositeLabel) : defaultOppositeLabel;

  const books: BookOddsData[] = [];
  let bestSelectionOdds: number | null = null;
  let bestOppositeOdds: number | null = null;
  let hasAnyOppositeOdds = false;

  for (const bookId of orderedBooks) {
    let selectionOdds: number | null = null;
    let oppositeOdds: number | null = null;

    if (isOverUnder) {
      const overOdds = getBookOdds(row, bookId, "over");
      const underOdds = getBookOdds(row, bookId, "under");
      selectionOdds = isOver ? overOdds : underOdds;
      oppositeOdds = isOver ? underOdds : overOdds;
    } else if (isHomeAway) {
      selectionOdds = getBookMainOdds(row, bookId);
      const overOdds = getBookOdds(row, bookId, "over");
      const underOdds = getBookOdds(row, bookId, "under");
      oppositeOdds = betType === "HOME" ? underOdds : overOdds;
    } else {
      selectionOdds = getBookMainOdds(row, bookId);
      oppositeOdds = null;
    }

    const postedOdds = selectionOdds;
    if (bookId === row.primary_book && row.slip_implied_odds != null) {
      selectionOdds = row.slip_implied_odds;
      if (oppositeOdds !== null) oppositeOdds = row.slip_implied_odds;
    }

    if (selectionOdds === null && oppositeOdds === null) {
      continue;
    }

    if (oppositeOdds !== null) {
      hasAnyOppositeOdds = true;
    }

    const bookLine = row.__comparison_lines?.[bookId];
    const hasLineDiff =
      typeof bookLine === "number" && Number.isFinite(bookLine);

    if (!hasLineDiff && selectionOdds !== null) {
      if (bestSelectionOdds === null || selectionOdds > bestSelectionOdds) {
        bestSelectionOdds = selectionOdds;
      }
    }
    if (!hasLineDiff && oppositeOdds !== null) {
      if (bestOppositeOdds === null || oppositeOdds > bestOppositeOdds) {
        bestOppositeOdds = oppositeOdds;
      }
    }

    books.push({
      book: bookId,
      selectionId: row.__comparison_selection_ids?.[bookId] ?? null,
      selectionOdds,
      oppositeOdds,
      line: hasLineDiff ? bookLine : undefined,
      stake: rowStakeQuote(row, bookId, postedOdds),
      oppositeStake: oppositeOdds === null ? null : oppositeStake(row, bookId),
      betLink: resolveBetLink(row.__comparison_links?.[bookId], isMobile),
    });
  }

  const singleSideOnly = !hasAnyOppositeOdds;

  for (const book of books) {
    book.isBestSelection =
      book.selectionOdds !== null &&
      book.line === undefined &&
      book.selectionOdds === bestSelectionOdds;
    book.isBestOpposite =
      book.oppositeOdds !== null &&
      book.line === undefined &&
      book.oppositeOdds === bestOppositeOdds;
  }

  const title = row.player_name || row.event_name || "Unknown";
  const stat = row.stat_type || row.market_display || "";
  const lineStr = row.line != null ? String(row.line) : "";
  const subtitle = singleSideOnly
    ? stat || ""
    : stat ? `${defaultSelectionLabel} ${lineStr} ${stat}`.trim() : `${defaultSelectionLabel} ${lineStr}`.trim();

  return {
    title,
    subtitle,
    selectionLabel,
    oppositeLabel,
    mainLine: row.line ?? undefined,
    books,
    primaryBook,
    bestSelectionOdds,
    bestOppositeOdds,
    singleSideOnly,
  };
}
